import { fabric } from "fabric";
import { ObjectSuperTypesEnum } from "../../../enums/ObjectSuperTypesEnum";
import { RowSeatLayoutHelper } from "./RowSeatLayoutHelper";

export class RowSpacingHelper {
  static setSpacing(selection: fabric.ActiveSelection, spacing: number): boolean {
    if (!Number.isFinite(spacing) || spacing < 0) return false;
    const layout = this.getLayout(selection);
    if (!layout) return false;
    const { normalX, normalY, selectionMatrix, orderedRows } = layout;
    const inverse = fabric.util.invertTransform(selectionMatrix);
    let previousEnd = orderedRows[0].end;

    orderedRows.slice(1).forEach(({ row, start, end }) => {
      const distance = previousEnd + spacing - start;
      const offset = fabric.util.transformPoint(
        new fabric.Point(normalX * distance, normalY * distance), inverse, true
      );
      row.set({ left: row.left + offset.x, top: row.top + offset.y });
      row.setCoords();
      previousEnd = end + distance;
    });

    selection.addWithUpdate();
    selection.setCoords();
    return true;
  }

  static getSpacing(selection: fabric.ActiveSelection): number | undefined {
    const layout = this.getLayout(selection);
    if (!layout) return undefined;
    const { orderedRows } = layout;
    const spacing = orderedRows[1].start - orderedRows[0].end;
    // Leave the input empty when the selection has different gaps or overlaps.
    if (spacing < -1e-6 || orderedRows.slice(1).some((row, index) =>
      Math.abs(row.start - orderedRows[index].end - spacing) > 1e-6)) {
      return undefined;
    }
    return Math.max(0, Math.round(spacing * 1000000) / 1000000);
  }

  private static getLayout(selection: fabric.ActiveSelection) {
    const rows = selection.getObjects() as any[];
    if (rows.length < 2 ||
      !rows.every(row => row.superType === ObjectSuperTypesEnum.Row)) {
      return null;
    }

    const reference = rows[0];
    const seats = reference.getObjects().filter(
      (object: any) => object.superType === ObjectSuperTypesEnum.Seat
    );
    const direction = RowSeatLayoutHelper.getRowDirection(seats.map((seat: any) => ({
      getCenterPoint: () => {
        const shape = seat.getObjects?.()[0] ?? seat;
        return fabric.util.transformPoint(new fabric.Point(0, 0), shape.calcTransformMatrix());
      },
    })));
    const angle = fabric.util.degreesToRadians(reference.angleHelp || 0);
    const rowMatrix = reference.calcTransformMatrix();
    const axis = direction ? new fabric.Point(direction.ux, direction.uy) :
      fabric.util.transformPoint(new fabric.Point(Math.cos(angle), Math.sin(angle)), rowMatrix, true);
    const length = Math.hypot(axis.x, axis.y);
    if (!length) return null;

    // Order by position perpendicular to the rows, regardless of selection order.
    let normalX = -axis.y / length;
    let normalY = axis.x / length;
    if (normalY < -1e-8 || (Math.abs(normalY) <= 1e-8 && normalX < 0)) {
      normalX *= -1;
      normalY *= -1;
    }

    const selectionMatrix = selection.calcTransformMatrix();
    const orderedRows = rows.map(row => {
      const center = fabric.util.transformPoint(row.getCenterPoint(), selectionMatrix);
      const rowSeats = row.getObjects().filter(
        (object: any) => object.superType === ObjectSuperTypesEnum.Seat
      );
      // Match MultipleRows: center distance = seat size + stroke + row spacing.
      // Measure the seat shape including its stroke, excluding labels and icons.
      const projections: number[] = [];
      (rowSeats.length ? rowSeats : [row]).forEach((object: any) => {
        const shape = object.getObjects?.()[0] ?? object;
        const matrix = shape.calcTransformMatrix();
        if (shape.type === "circle") {
          const center = fabric.util.transformPoint(new fabric.Point(0, 0), matrix);
          const position = center.x * normalX + center.y * normalY;
          // Project the circle (or scaled ellipse), independent of seat rotation.
          const radius = (shape.radius + (shape.strokeWidth || 0) / 2) * Math.hypot(
            matrix[0] * normalX + matrix[1] * normalY,
            matrix[2] * normalX + matrix[3] * normalY
          );
          projections.push(position - radius, position + radius);
          return;
        }
        const halfWidth = (shape.width + (shape.strokeWidth || 0)) / 2;
        const halfHeight = (shape.height + (shape.strokeWidth || 0)) / 2;
        [-halfWidth, halfWidth].forEach(x => [-halfHeight, halfHeight].forEach(y => {
          const point = fabric.util.transformPoint(new fabric.Point(x, y), matrix);
          projections.push(point.x * normalX + point.y * normalY);
        }));
      });
      return {
        row, position: center.x * normalX + center.y * normalY,
        start: Math.min(...projections), end: Math.max(...projections),
      };
    }).sort((a, b) => a.position - b.position);
    return { normalX, normalY, selectionMatrix, orderedRows };
  }
}
